import { selectSelectedProperty } from 'core/core.reducer';
import type { ActionsObservable, StateObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import type { AppState } from 'redux/app-state';
import { concat, of } from 'rxjs';
import { catchError, concatMap, flatMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import type { DeleteUploadFile, LoadUploadFileImages, PostUploadFile, UpdateUploadFile } from './upload-file.actions';
import {
  DeleteUploadFileFailure,
  DeleteUploadFileSuccess,
  LoadUploadFileImagesFailure,
  LoadUploadFileImagesSuccess,
  LoadUploadFileList,
  LoadUploadFileListFailure,
  LoadUploadFileListSuccess,
  PostUploadFileFailure,
  PostUploadFileSuccess,
  UpdateUploadFileFailure,
  UpdateUploadFileSuccess,
  UploadFileActionsTypes
} from './upload-file.actions';
import {
  deleteUploadFileListImages,
  getUploadFileList,
  getUploadFileListImages,
  postFile,
  putUploadFileListImages
} from './upload-file.service';

export const handleLoadUploadFileList = (
  action$: ActionsObservable<ReturnType<typeof LoadUploadFileList>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(UploadFileActionsTypes.LOAD_UPLOAD_FILE_LIST),
    withLatestFrom(state$.pipe(map(state => selectSelectedProperty(state.uiState.global)))),
    concatMap(([, selectedProperty]) => {
      if (!selectedProperty) return of(LoadUploadFileListFailure('No property selected'));
      return getUploadFileList(selectedProperty).pipe(
        map(response => response.data),
        flatMap(list => {
          return concat([LoadUploadFileListSuccess(list.content)]);
        }),
        catchError((error: string) => of(LoadUploadFileListFailure(error)))
      );
    })
  );

export const handleDeleteUploadFile = (action$: ActionsObservable<ReturnType<typeof DeleteUploadFile>>) =>
  action$.pipe(
    ofType(UploadFileActionsTypes.DELETE_UPLOAD_FILE),
    map(action => action.payload),
    concatMap(request => {
      return deleteUploadFileListImages(request).pipe(
        map(response => response.data),
        flatMap(() => {
          return concat([LoadUploadFileList(), DeleteUploadFileSuccess()]);
        }),
        catchError((error: string) => of(DeleteUploadFileFailure(error)))
      );
    })
  );

export const handleUpdateUploadFile = (action$: ActionsObservable<ReturnType<typeof UpdateUploadFile>>) =>
  action$.pipe(
    ofType(UploadFileActionsTypes.UPDATE_UPLOAD_FILE),
    map(action => action.payload),
    concatMap(request => {
      return putUploadFileListImages(request.id, request).pipe(
        map(response => response.data),
        flatMap(() => {
          return concat([LoadUploadFileList(), UpdateUploadFileSuccess()]);
        }),
        catchError((error: string) => of(UpdateUploadFileFailure(error)))
      );
    })
  );

export const handleLoadUploadFileListImages = (action$: ActionsObservable<ReturnType<typeof LoadUploadFileImages>>) =>
  action$.pipe(
    ofType(UploadFileActionsTypes.LOAD_UPLOAD_FILE_IMAGES),
    map(action => action.payload),
    mergeMap(payload => {
      return getUploadFileListImages(payload).pipe(
        map(response => response.data),
        flatMap(file => {
          return concat([LoadUploadFileImagesSuccess(file, payload)]);
        }),
        catchError((error: string) => of(LoadUploadFileImagesFailure(error)))
      );
      // This is a temporary fix to avoid overload Gaia service
    }, 1)
  );

export const handlePostUploadFile = (action$: ActionsObservable<ReturnType<typeof PostUploadFile>>) =>
  action$.pipe(
    ofType(UploadFileActionsTypes.POST_UPLOAD_FILE),
    map(action => action.payload),
    concatMap(payload => {
      return postFile(payload).pipe(
        map(response => response.data),
        flatMap(metadata => {
          return concat([LoadUploadFileList(), PostUploadFileSuccess(metadata)]);
        }),
        catchError((error: string) => of(PostUploadFileFailure(error)))
      );
    })
  );
